import { prop } from 'ramda';
import { useTranslations } from '@core/js/hooks/i18n';
import React, { memo, useCallback } from 'react';
import { useGameConfigurationSelector, useDeviceSelector, useAppSelector } from '@core/js/store/hooks';
import { AccessibilityElement } from '@core/js/components/common/AccessibilityElement';

import * as Types from '@core/js/components/main/GamesGrid/components/Game/components/GameViewLive/types';
import { GameName } from '@core/js/components/main/GamesGrid/components/Game/components/GameName';
import { GameLabels } from '@core/js/components/main/GamesGrid/components/Game/components/GameLabels';
import { GameJackpot } from '@core/js/components/main/GamesGrid/components/Game/components/GameJackpot';
import { GameProvider } from '@core/js/components/main/GamesGrid/components/Game/components/GameProvider';
import { GameMoreInfo } from '@core/js/components/main/GamesGrid/components/Game/components/GameMoreInfo';
import { GamePlayButton } from '@core/js/components/main/GamesGrid/components/Game/components/GamePlayButton';
import { GameLiveLimits } from '@core/js/components/main/GamesGrid/components/Game/components/GameLiveLimits';
import { GameLiveOpenDate } from '@core/js/components/main/GamesGrid/components/Game/components/GameLiveOpenDate';
import { GameLiveLanguage } from '@core/js/components/main/GamesGrid/components/Game/components/GameLiveLanguage';
import { GameFavoriteIcon } from '@core/js/components/main/GamesGrid/components/Game/components/GameFavoriteIcon';
import { GameLiveDealerName } from '@core/js/components/main/GamesGrid/components/Game/components/GameLiveDealerName';
import { GameViewCustomHover } from '@core/js/components/main/GamesGrid/components/Game/components/GameViewCustomHover';
import { GameLivePlayersNumber } from '@core/js/components/main/GamesGrid/components/Game/components/GameLivePlayersNumber';
import { GameMinMaxBet } from '../GameMinMaxBet';

import style from './GameViewLive.scss';

export const GameViewLiveBare: React.FC<Types.IGameViewLiveProps> = ({ game }) => {
    const translate = useTranslations();
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const isMinMaxBetEnabled = dynamicTags?.DTAG_SHOW_MIN_MAX_BET === 'true';
    const {
        isLabelsEnabled,
        isJackpotEnabled,
        isGameNameEnabled,
        isGameInfoEnabled,
        isFavoritesEnabled,
        isProviderNameEnabled,
        isHoverGameNameEnabled,
        gameTileLayoutHoverType,
        isDealerNameEnabled,
        isLanguageEnabled,
        isLimitsEnabled,
        isPlayersNumberEnabled,
        isJackpotHitTimeEnabled
    } = useGameConfigurationSelector(prop('liveGameTileLayout')) || {};
    const formFactor = useDeviceSelector(prop('formFactor'));

    // since we use focus-within CSS selector, we need to blur the button on click otherwise it will remain focused
    const handleGameClick = useCallback(() => {
        if (document.activeElement instanceof HTMLElement && formFactor === 'desktop') {
            document.activeElement.blur();
        }
    }, [formFactor]);

    const isOpenedTable = !game?.liveConfiguration?.isScheduled;

    return (
        <AccessibilityElement
            title={game?.name}
            onClick={handleGameClick}
            className={`${style.container} cms-games-grid-game-live-view`}
            aria-label={`${translate('playtech.mobile.system.games-grid.game.aria-label')} ${game.name}`}
        >
            {/* Lugano changes - Start */}
            <div className={`${style.activeContainer} cms-games-grid-game-main-state`}>
                <div className={style.topContainer}>
                    <div className={style.gameLabelsLeftContainer}>
                        {isLimitsEnabled && isOpenedTable && <GameLiveLimits game={game} className={style.liveLabel} />}
                        {isPlayersNumberEnabled && isOpenedTable && (
                            <GameLivePlayersNumber game={game} className={style.liveLabel} />
                        )}
                    </div>
                    <div className={style.gameLabelsRightContainer}>
                        {isDealerNameEnabled && isOpenedTable && <GameLiveDealerName game={game} className={style.liveLabel} />}
                        {isLanguageEnabled && isOpenedTable && <GameLiveLanguage game={game} className={style.liveLabel} />}
                    </div>
                </div>
                <div className={style.mainStateContainer}>
                    {isGameNameEnabled && <GameName game={game} className={style.gameName} />}
                    {isProviderNameEnabled &&
                        <GameProvider game={game} className={style.gameProvider} />
                    }
                    {isJackpotEnabled && (
                        <GameJackpot
                            game={game}
                            className={style.jackpot}
                            isHitTimeEnabled={isJackpotHitTimeEnabled}
                        />
                    )}
                </div>
                <div className={style.bottomContainer}>
                    {isLabelsEnabled && <GameLabels game={game} className={style.gameLabels} />}
                </div>
                {!isOpenedTable && <GameLiveOpenDate game={game} className={style.openTable} />}
            </div>
            <div className={`${style.hoverContainer} cms-games-grid-game-hover-state`}>
                {gameTileLayoutHoverType === 'custom' && <GameViewCustomHover game={game} />}
                {gameTileLayoutHoverType !== 'custom' && (
                    <div className={`${style.hoverContainerDefault} cms-games-grid-game-hover-state-default`}>
                        <div className={style.hoverHeader}>
                            {isGameInfoEnabled && <GameMoreInfo game={game} />}
                        </div>
                        <div className={style.hoverControls}>
                            {isOpenedTable && <GamePlayButton game={game} />}
                            {isHoverGameNameEnabled &&
                                <GameName game={game} className={style.hoverGameName} />
                            }
                            {isMinMaxBetEnabled && <GameMinMaxBet game={game} />}
                            {!isOpenedTable && <GameLiveOpenDate game={game} className={style.openTableHover} />}
                        </div>
                    </div>
                )}
            </div>
            {isFavoritesEnabled && <GameFavoriteIcon game={game} className={style.gameFavoriteIcon} />}
            {/* Lugano changes - End */}
        </AccessibilityElement>
    );
};

export const GameViewLive = memo(GameViewLiveBare);
